import axios, { AxiosRequestConfig } from 'axios';
import { Contact, ContactFormData, ContactFilters, PaginatedResponse } from '../types/contact';
import { Branch, BranchFormData, BranchFilters, PaginatedBranchResponse } from '../types/branch';
import { Warehouse, WarehouseFormData, WarehouseFilters, PaginatedWarehouseResponse } from '../types/warehouse';
import { User, UserFormData, UserFilters, PaginatedUsersResponse } from '../types/user';
import { WarehouseTransfer, WarehouseTransferFormData, WarehouseTransferFilters, PaginatedWarehouseTransferResponse } from '../types/warehouseTransfer';

// Extend AxiosRequestConfig to include permissionContext
interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  permissionContext?: string;
}

// Mock user data for development - matches what we use in setupProxy.js
const MOCK_USER_ID = "64067927a9259bbde06c6f15";
const MOCK_BRANCH_ID = "6406aca7a9259bbde06c755e";

// Use environment variable for API URL
const baseURL = process.env.REACT_APP_API_URL;

if (!baseURL) {
  console.error('⚠️ تحذير: لم يتم تعيين REACT_APP_API_URL في ملف .env');
  console.error('⚠️ يرجى إضافة REACT_APP_API_URL=http://your-server-url/api في ملف client/.env');
}

console.log('API base URL:', baseURL);

const api = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Add a request interceptor to inject auth headers
api.interceptors.request.use(
  config => {
    console.log(`Making ${config.method?.toUpperCase()} request to ${config.url}`);
    
    // Add auth headers to every request
    config.headers = config.headers || {};
    
    // Add authentication token from localStorage - always get the latest token
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['x-auth-token'] = token;
      config.headers['Authorization'] = `Bearer ${token}`;
      
      // Also update global axios defaults to ensure consistency
      axios.defaults.headers.common['x-auth-token'] = token;
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    
    // Add user and branch IDs
    config.headers['X-User-ID'] = MOCK_USER_ID;
    config.headers['X-Branch-ID'] = localStorage.getItem('activeBranchId') || localStorage.getItem('currentBranchId') || MOCK_BRANCH_ID;
    
    // Add permission context header if provided in config
    if ((config as ExtendedAxiosRequestConfig).permissionContext) {
      config.headers['X-Permission-Context'] = (config as ExtendedAxiosRequestConfig).permissionContext;
      console.log(`[API_INTERCEPTOR] Adding permission context: ${(config as ExtendedAxiosRequestConfig).permissionContext} to ${config.url}`);
      console.log(`[API_INTERCEPTOR] All headers:`, config.headers);
    } else {
      console.log(`[API_INTERCEPTOR] No permission context for ${config.url}`);
    }
    
    // If this is a request to create or update an invoice, ensure branchId and userId are in the body
    if ((config.url?.includes('/invoices') && (config.method === 'post' || config.method === 'patch' || config.method === 'put')) && config.data) {
      if (typeof config.data === 'object') {
        config.data.branchId = MOCK_BRANCH_ID;
        config.data.createdBy = MOCK_USER_ID;
        
        console.log('Enhanced request body:', JSON.stringify(config.data, null, 2));
      }
    }
    
    return config;
  },
  error => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

export const contactsApi = {
  // Get contacts with filtering and pagination
  getContacts: async (filters: ContactFilters): Promise<PaginatedResponse> => {
    const params = new URLSearchParams();
    
    // Process each filter property
    Object.entries(filters).forEach(([key, value]) => {
      // Skip empty/null values
      if (!value) return;
      
      // إرسال جميع الفلاتر كما هي بدون تعديل
      params.append(key, value.toString());
    });
    
    const response = await api.get(`/contacts?${params.toString()}`);
    return response.data;
  },

  // Get single contact by ID
  getContact: async (id: string): Promise<Contact> => {
    const response = await api.get(`/contacts/${id}`);
    return response.data;
  },

  // Create new contact
  createContact: async (contactData: ContactFormData): Promise<Contact> => {
    const response = await api.post('/contacts', contactData);
    return response.data;
  },

  // Update existing contact
  updateContact: async (id: string, contactData: ContactFormData): Promise<Contact> => {
    const response = await api.put(`/contacts/${id}`, contactData);
    return response.data;
  },

  // Delete contact
  deleteContact: async (id: string): Promise<void> => {
    await api.delete(`/contacts/${id}`);
  },

  // Add note to contact
  addNote: async (id: string, content: string): Promise<Contact> => {
    const response = await api.post(`/contacts/${id}/notes`, { content });
    return response.data;
  },
  
  // Bulk import contacts
  importContacts: async (contacts: ContactFormData[]): Promise<any> => {
    const response = await api.post('/contacts/import', { contacts });
    return response.data;
  }
};

// Add response interceptor for error handling
api.interceptors.response.use(
  response => response,
  error => {
    let message;
    console.error('API Error:', error);
    
    // إذا كان الخطأ هو 401 (غير مصرح به)، تعامل معه بشكل خاص
    if (error.response?.status === 401) {
      console.warn('خطأ مصادقة 401: رمز المصادقة غير صالح أو انتهت صلاحيته');
      
      // تأكد من عدم وجود المستخدم بالفعل في صفحة تسجيل الدخول
      const currentPath = window.location.pathname;
      if (!currentPath.includes('/login')) {
        // حذف الرمز غير الصالح من التخزين المحلي
        localStorage.removeItem('token');
        localStorage.removeItem('activeBranchId');
        
        // عرض رسالة للمستخدم
        if (!(window as any).isShowingAuthAlert) {
          (window as any).isShowingAuthAlert = true;
          
          // تحديد نوع الخطأ بناءً على URL
          let errorMsg = 'انتهت جلسة العمل. يرجى تسجيل الدخول مرة أخرى للمتابعة.';
          
          alert(errorMsg);
          
          // إعادة توجيه المستخدم إلى صفحة تسجيل الدخول
          // تخزين المسار الحالي للعودة إليه بعد تسجيل الدخول
          localStorage.setItem('redirect_after_login', currentPath);
          window.location.href = '/login';
          
          // وقف المزيد من المعالجة
          return new Promise(() => {});
        }
      }
      
      message = 'جلسة العمل منتهية. يرجى تسجيل الدخول مرة أخرى.';
    } else if (error.response?.data?.errors) {
      // معالجة أخطاء التحقق من الصحة
      message = error.response.data.errors.map((err: any) => err.msg).join(', ');
    } else if (error.response?.data?.error) {
      message = error.response.data.error;
      // تضمين تفاصيل إضافية إذا كانت متوفرة
      if (error.response.data.details) {
        console.error('تفاصيل الخطأ:', error.response.data.details);
        message += ` - ${error.response.data.details}`;
      }
    } else {
      message = 'حدث خطأ في النظام';
    }
    
    console.error('رسالة الخطأ:', message);
    throw new Error(message);
  }
);

export const branchesApi = {
  // Get branches with filtering and pagination
  getBranches: async (filters: BranchFilters): Promise<PaginatedBranchResponse> => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value.toString());
    });
    const response = await api.get(`/branches?${params.toString()}`);
    return response.data;
  },

  // Get single branch by ID
  getBranch: async (id: string): Promise<Branch> => {
    const response = await api.get(`/branches/${id}`);
    return response.data;
  },

  // Create new branch
  createBranch: async (branchData: BranchFormData): Promise<Branch> => {
    // Ensure defaultPosPage is properly included
    console.log('Creating branch with data:', JSON.stringify(branchData, null, 2));
    
    // Make sure defaultPosPage is explicitly included in the request
    const requestData = {
      ...branchData,
      defaultPosPage: branchData.defaultPosPage || ''
    };
    
    try {
      const response = await api.post('/branches', requestData);
      console.log('Branch created successfully, response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating branch:', error);
      throw error;
    }
  },

  // Update existing branch
  updateBranch: async (id: string, branchData: BranchFormData): Promise<Branch> => {
    // Ensure defaultPosPage is properly included
    console.log(`Updating branch ${id} with data:`, JSON.stringify(branchData, null, 2));
    
    // Make sure defaultPosPage is explicitly included in the request
    const requestData = {
      ...branchData,
      defaultPosPage: branchData.defaultPosPage || ''
    };
    
    try {
      const response = await api.put(`/branches/${id}`, requestData);
      console.log('Branch updated successfully, response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating branch:', error);
      throw error;
    }
  },

  // Delete branch
  deleteBranch: async (id: string): Promise<void> => {
    await api.delete(`/branches/${id}`);
  }
};

export const warehousesApi = {
  // Get warehouses with filtering and pagination
  getWarehouses: async (filters: WarehouseFilters = {}): Promise<PaginatedWarehouseResponse> => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value.toString());
    });
    const response = await api.get(`/warehouses?${params.toString()}`);
    return response.data;
  },

  // Get single warehouse by ID
  getWarehouse: async (id: string): Promise<Warehouse> => {
    const response = await api.get(`/warehouses/${id}`);
    return response.data;
  },

  // Create new warehouse
  createWarehouse: async (warehouseData: WarehouseFormData): Promise<Warehouse> => {
    const response = await api.post('/warehouses', warehouseData);
    return response.data;
  },

  // Update existing warehouse
  updateWarehouse: async (id: string, warehouseData: WarehouseFormData): Promise<Warehouse> => {
    const response = await api.put(`/warehouses/${id}`, warehouseData);
    return response.data;
  },

  // Delete warehouse
  deleteWarehouse: async (id: string): Promise<void> => {
    await api.delete(`/warehouses/${id}`);
  },

  // Get warehouse transfers with filtering and pagination
  getTransfers: async (filters: WarehouseTransferFilters = {}): Promise<PaginatedWarehouseTransferResponse> => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value.toString());
    });
    try {
      console.log('Calling API: /warehouse-transfers');
      const response = await api.get(`/warehouse-transfers?${params.toString()}`);
      console.log('Raw API response data:', response.data);
      
      // Normalize the response to ensure it matches expected format
      const normalizedResponse: PaginatedWarehouseTransferResponse = {
        transfers: response.data.transfers || [],
        pagination: response.data.pagination || {
          total: 0,
          page: 1,
          pages: 1
        }
      };
      
      return normalizedResponse;
    } catch (error) {
      console.error('Error in getTransfers API call:', error);
      throw error;
    }
  },

  // Get single warehouse transfer by ID
  getTransfer: async (id: string): Promise<WarehouseTransfer> => {
    const response = await api.get(`/warehouse-transfers/${id}`);
    return response.data;
  },

  // Create new warehouse transfer
  createTransfer: async (transferData: WarehouseTransferFormData): Promise<WarehouseTransfer> => {
    const response = await api.post('/warehouse-transfers', transferData);
    return response.data;
  },

  // Update existing warehouse transfer
  updateTransfer: async (id: string, transferData: WarehouseTransferFormData): Promise<WarehouseTransfer> => {
    const response = await api.put(`/warehouse-transfers/${id}`, transferData);
    return response.data;
  },

  // Delete warehouse transfer
  deleteTransfer: async (id: string): Promise<void> => {
    await api.delete(`/warehouse-transfers/${id}`);
  }
};

export const inventoryApi = {
  // Search inventory
  searchInventory: async (term: string, warehouseId: string, includeDetails: boolean = false, includeVariants: boolean = false): Promise<any> => {
    const params = new URLSearchParams();
    params.append('term', term);
    params.append('warehouse', warehouseId);
    params.append('includeDetails', includeDetails.toString());
    params.append('includeVariants', includeVariants.toString());
    
    const response = await api.get(`/inventory/search?${params.toString()}`);
    return response.data;
  },
  
  // Get warehouse inventory
  getWarehouseInventory: async (warehouseId: string): Promise<any> => {
    const response = await api.get(`/inventory/warehouse/${warehouseId}`);
    return response.data;
  }
};

export const usersApi = {
  // Get users with filtering and pagination
  getUsers: async (filters: UserFilters): Promise<PaginatedUsersResponse> => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value.toString());
    });
    const response = await api.get(`/users?${params.toString()}`);
    return response.data;
  },

  // Get single user by ID
  getUser: async (id: string): Promise<User> => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  // Get all branches for user assignment
  getBranches: async (): Promise<Array<{_id: string, name: string, address?: string}>> => {
    const response = await api.get('/users/branches');
    return response.data;
  },

  // Create new user
  createUser: async (userData: UserFormData): Promise<User> => {
    const response = await api.post('/users', userData);
    return response.data;
  },

  // Update existing user
  updateUser: async (id: string, userData: UserFormData): Promise<User> => {
    const response = await api.put(`/users/${id}`, userData);
    return response.data;
  },
  
  // تحديث الحسابات المتاحة للمستخدم
  updateUserAccessibleAccounts: async (id: string, accountIds: string[]): Promise<User> => {
    const response = await api.put(`/users/${id}/accounts`, { accessibleAccounts: accountIds });
    return response.data;
  },

  // Delete user
  deleteUser: async (id: string): Promise<void> => {
    await api.delete(`/users/${id}`);
  },
  
  // إعدادات الحسابات المرتبطة
  linkedAccountSettings: {
    // الحصول على إعدادات الحسابات المرتبطة
    getSettings: async (): Promise<any> => {
      const response = await api.get('/finance/settings/linked-accounts');
      return response.data;
    },

    // تحديث إعدادات الحسابات المرتبطة
    updateSettings: async (data: { customerDefaultAccount?: string, supplierDefaultAccount?: string }): Promise<any> => {
      const response = await api.post('/finance/settings/linked-accounts', data);
      return response.data;
    }
  }
};

// Helper functions for making requests with permission context
export const apiWithContext = {
  get: (url: string, context?: string, options: AxiosRequestConfig = {}) => {
    console.log(`[API_WITH_CONTEXT] Making GET request to ${url} with context: ${context}`);
    const config: ExtendedAxiosRequestConfig = {
      ...options,
      permissionContext: context,
      headers: {
        ...options.headers,
        ...(context && { 'X-Permission-Context': context })
      }
    };
    console.log(`[API_WITH_CONTEXT] Final config headers:`, config.headers);
    return api.get(url, config);
  },
  
  post: (url: string, data?: any, context?: string, options: AxiosRequestConfig = {}) => {
    console.log(`[API_WITH_CONTEXT] Making POST request to ${url} with context: ${context}`);
    const config: ExtendedAxiosRequestConfig = {
      ...options,
      permissionContext: context,
      headers: {
        ...options.headers,
        ...(context && { 'X-Permission-Context': context })
      }
    };
    return api.post(url, data, config);
  },
  
  put: (url: string, data?: any, context?: string, options: AxiosRequestConfig = {}) => {
    console.log(`[API_WITH_CONTEXT] Making PUT request to ${url} with context: ${context}`);
    const config: ExtendedAxiosRequestConfig = {
      ...options,
      permissionContext: context,
      headers: {
        ...options.headers,
        ...(context && { 'X-Permission-Context': context })
      }
    };
    return api.put(url, data, config);
  },
  
  delete: (url: string, context?: string, options: AxiosRequestConfig = {}) => {
    console.log(`[API_WITH_CONTEXT] Making DELETE request to ${url} with context: ${context}`);
    const config: ExtendedAxiosRequestConfig = {
      ...options,
      permissionContext: context,
      headers: {
        ...options.headers,
        ...(context && { 'X-Permission-Context': context })
      }
    };
    return api.delete(url, config);
  }
};

export default api;